import { IColumn } from '@fluentui/react';
import { IPFontIcon, IPSelectionMode, IPSeparator, IPText } from 'baseComponents';
import { IPStack } from 'baseComponents/Stack/IPStack';
import GenericModal from 'components/Dialogs/GenericModal';
import BaseTableList from 'components/TableList/BaseTableList';
import ScrollableStackItem from 'components/Utils/ScrollableStackItem';
import { checkIcon, errorIcon, globalStackTokensGapSmall } from 'globalStyles';
import { PublicationResult, PublicationResultStatus } from 'models/resourceLink';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LocalStorageKeys } from 'utils/localstorage';
import { sortOnNumber, sortOnString } from 'utils/sorting';

interface ILinkPublishResultsModalProps {
  isOpen: boolean;
  results?: PublicationResult[];
  onClose: () => void;
}

export const LinkPublishResultsModal = (props: ILinkPublishResultsModalProps) => {
  const { t } = useTranslation(['translation', 'publication', 'sharepoint']);

  //
  // Columns list view
  //
  const getColumns = (): IColumn[] => {
    const allColumns: IColumn[] = [
      {
        key: 'name',
        name: t('publication:Publish.PublicationResultsModal.Columns.Name'),
        minWidth: 150,
        maxWidth: 400,
        isRowHeader: true,
        isResizable: true,
        isMultiline: true,
      },
      {
        key: 'status',
        name: t('publication:Publish.PublicationResultsModal.Columns.Status'),
        minWidth: 150,
        maxWidth: 400,
        isResizable: true,
        isMultiline: true,
      },
    ];

    return allColumns;
  };

  const [columnsList, setColumnsList] = useState<IColumn[]>(getColumns());

  const getStatusIcon = (status?: PublicationResultStatus): JSX.Element => {
    switch (status) {
      case PublicationResultStatus.Success:
        return <IPFontIcon {...checkIcon} style={{ fontSize: 18, color: 'green' }} />;
      default:
        return <IPFontIcon {...errorIcon} style={{ fontSize: 18, color: 'red' }} />;
    }
  };

  const onRenderCol = (item: PublicationResult, column: IColumn): JSX.Element | null => {
    if (!item) return null;

    switch (column.key) {
      case 'name':
        return <IPText>{item.link?.linkName}</IPText>;
      case 'status':
        return (
          <IPStack horizontal verticalAlign="center" tokens={globalStackTokensGapSmall}>
            {getStatusIcon(item.status)}
            <IPText>{item.getPublishResultStatusText(t)}</IPText>
            {item.status === PublicationResultStatus.Success && (
              <IPText>
                {t('publication:Publish.PublicationResultsModal.PublishedAsVersion', { version: item.newVersion })}
              </IPText>
            )}
          </IPStack>
        );
      default:
        return null;
    }
  };

  const onCompare = (a: PublicationResult, b: PublicationResult, columnKey: string): number => {
    switch (columnKey) {
      case 'name':
        return sortOnString(a.link?.linkName, b.link?.linkName);
      case 'status':
        return sortOnNumber(a.status, b.status);
      default:
        return 0;
    }
  };

  //
  // Main render
  //
  return (
    <GenericModal
      isOpen={props.isOpen}
      onClose={props.onClose}
      title={t('publication:Publish.PublicationResultsModal.Title')}
      subTitle={t('publication:Publish.PublicationResultsModal.SubText')}
      height={500}
      width={900}
      maxWidth={'min(90vw, 900px)'}
      maxHeight={'90vh'}
      minHeight={300}
      minWidth={300}
    >
      <IPStack verticalFill>
        <IPStack.Item>
          <IPSeparator />
        </IPStack.Item>
        <ScrollableStackItem>
          <BaseTableList
            compact
            items={props.results || []}
            columns={columnsList}
            selectionMode={IPSelectionMode.none}
            onRenderCol={onRenderCol}
            compare={onCompare}
            setColumns={setColumnsList}
            cacheKeySort={LocalStorageKeys.LinkPublishResults}
          />
        </ScrollableStackItem>
      </IPStack>
    </GenericModal>
  );
};
